import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/comics';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9p1fo0senbniolesnv0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    if (!CONTACT_FORM_URL) {
      setFormStatus('error');
      setFormError('表单服务暂未配置，请稍后再试。');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试。';
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解无遮韩国免费漫画大全 - 国内领先的韩漫漫画网站。提供品牌介绍、信誉展示和联系方式。免费在线阅读韩漫，网站稳定性高。"
        keywords="关于无遮韩国免费漫画大全,韩漫网站介绍,韩漫平台,免费韩漫网站,联系我们"
        canonicalUrl="/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-5xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                关于 {aboutInfo.brandName}
              </h1>
              <p className="text-base text-foreground-500 max-w-2xl mx-auto">
                {aboutInfo.tagline}
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-12">
                {aboutInfo.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70"
                  >
                    <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {aboutInfo.introduction}
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                    <strong>我们的使命：</strong>{aboutInfo.mission}
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    <strong>我们的愿景：</strong>{aboutInfo.vision}
                  </p>
                </div>

                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    核心价值观
                  </h2>
                  <div className="space-y-3">
                    {aboutInfo.values.map((v) => (
                      <div
                        key={v.title}
                        className="p-4 bg-background-50 rounded-lg border border-background-200/70"
                      >
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                          {v.title}
                        </h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">
                          {v.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  如有任何问题或建议，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">邮箱</h3>
                      <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors no-underline">
                        {aboutInfo.contact.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">电话</h3>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                      <i className="ri-wechat-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">微信</h3>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-foreground-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">地址</h3>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <h3 className="text-sm font-heading font-bold text-foreground-950 mb-4">
                    发送消息
                  </h3>

                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                        <i className="ri-check-line text-primary-600 text-2xl"></i>
                      </div>
                      <p className="text-sm font-medium text-foreground-950 mb-1">消息已发送</p>
                      <p className="text-xs text-foreground-500">感谢您的留言，我们会尽快回复！</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>

                      <div className="anti-spam-field">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      {formStatus === 'error' && formError && (
                        <p className="text-xs text-primary-600">{formError}</p>
                      )}

                      <button
                        type="submit"
                        className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}